import {client} from "../graphql/client";
import {gql} from "@apollo/client";
import remoteLogService from "../services/RemoteLogService";
import { isIOS } from './deviceUtils';
import { t } from '../i18n';

export const GET_FILE_UPLOAD_URL = gql`
  mutation StorageMutation($input: FileUrlInput!) {
    getFileUploadUrl(input: $input) {
      uploadUrl
      objectKey
      expiresAt
    }
  }
`;

const UPLOAD_REGISTRATION_FILE = gql`
  mutation UploadRegistrationFile($input: RegistrationFileUploadInput!) {
    uploadRegistrationFile(input: $input) {
      success
      objectKey
      message
    }
  }
`;

/**
 * 延迟函数
 * @param ms 延迟毫秒数
 */
const delay = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 上传文件并获取对象键
 * @param file 要上传的文件
 * @returns 包含对象键的Promise
 */
export const fileUpload = async (
    file: File,
    tag?: string
): Promise<{ objectKey: string }> => {
    // 获取上传URL
    const { data } = await client.mutate({
        mutation: GET_FILE_UPLOAD_URL,
        variables: {
            input: {
                filename: file.name,
                contentType: file.type,
                usage: tag ? `general/${tag}` : 'general'
            }
        }
    });

    if (!data?.getFileUploadUrl?.uploadUrl || !data?.getFileUploadUrl?.objectKey) {
        throw new Error('Failed to get upload URL');
    }

    const { uploadUrl, objectKey } = data.getFileUploadUrl;

    // 上传文件
    const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': 'multipart/form-data' },
    });

    if (!response.ok) {
        throw new Error('Failed to upload file');
    }

    return {
        objectKey
    };
};

/**
 * 将 File 转为 Base64 字符串（不含 data URI 前缀）
 */
const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(',');
            const base64 = comma >= 0 ? result.slice(comma + 1) : result;
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/** 注册场景文件最大大小：2MB */
const REGISTRATION_FILE_MAX_SIZE = 2 * 1024 * 1024;

/**
 * 注册场景文件上传（头像、车辆照片），文件以 Base64 传给后端，由后端完成上传
 * @param file 要上传的文件（不超过 2MB）
 * @param registrationToken 注册验证码校验通过时下发的一次性 token
 * @returns 包含对象键的Promise
 */
export const registrationFileUpload = async (
    file: File,
    registrationToken?: string | null,
): Promise<{ objectKey: string }> => {
    if (file.size > REGISTRATION_FILE_MAX_SIZE) {
        throw new Error('File size exceeds 2MB limit');
    }

    const fileBase64 = (await fileToBase64(file)).replace(/\s/g, '');

    const { data } = await client.mutate({
        mutation: UPLOAD_REGISTRATION_FILE,
        variables: {
            input: {
                fileBase64,
                registrationToken: registrationToken || null,
            }
        }
    });

    const result = data?.uploadRegistrationFile;
    if (!result?.success || !result?.objectKey) {
        throw new Error(result?.message || 'Failed to upload file');
    }

    return {
        objectKey: result.objectKey
    };
};

/** 与 PhotoSelector / H5 选图取消逻辑共用，便于按 code 判断 */
export const WEB_SELECTION_CANCELLED_CODE = 'SELECTION_CANCELLED';

const WEB_GALLERY_ACCEPT =
    'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';

export type PostProcessWebImageFn = (file: File) => Promise<File>;

/**
 * H5：通过隐藏 input[type=file] 选图（相册无 capture；拍照回退可传 environment）。
 * 压缩/校验由调用方 postProcessWebImage 完成。
 */
export const pickWebImageFile = (
    capture: 'environment' | undefined,
    postProcessWebImage: PostProcessWebImageFn,
): Promise<File> => {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = false;

        const appleH5 = isIOS();
        const enableDismissHeuristic = appleH5;

        if (capture) {
            input.accept = 'image/*';
            input.setAttribute('capture', capture);
        } else {
            input.accept = appleH5 ? 'image/*' : WEB_GALLERY_ACCEPT;
            input.removeAttribute('capture');
        }

        let settled = false;
        let dismissCheckTimer: number | undefined;
        let sawPickerBlurOrHidden = false;

        const rejectCancelled = () => {
            const err = new Error(t('pages.delivery.detail.photo.selectionCancelled'));
            (err as any).code = WEB_SELECTION_CANCELLED_CODE;
            reject(err);
        };

        const scheduleDismissCheck = () => {
            if (dismissCheckTimer !== undefined) {
                window.clearTimeout(dismissCheckTimer);
            }
            dismissCheckTimer = window.setTimeout(() => {
                dismissCheckTimer = undefined;
                if (settled) {
                    return;
                }
                const files = input.files;
                if (!files || files.length === 0) {
                    settled = true;
                    cleanup();
                    rejectCancelled();
                }
            }, 500);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sawPickerBlurOrHidden = true;
            } else if (document.visibilityState === 'visible' && sawPickerBlurOrHidden) {
                scheduleDismissCheck();
            }
        };

        const onWindowBlur = () => {
            sawPickerBlurOrHidden = true;
        };

        const onWindowFocus = () => {
            if (sawPickerBlurOrHidden) {
                scheduleDismissCheck();
            }
        };

        const timeout: number = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error(t('pages.delivery.detail.photo.fileSelectionTimeout')));
        }, 30000);

        const cleanup = () => {
            window.clearTimeout(timeout);
            if (dismissCheckTimer !== undefined) {
                window.clearTimeout(dismissCheckTimer);
            }
            if (enableDismissHeuristic) {
                document.removeEventListener('visibilitychange', onVisibilityChange);
                window.removeEventListener('blur', onWindowBlur, true);
                window.removeEventListener('focus', onWindowFocus, true);
            }
            input.removeEventListener('change', handleChange);
            input.removeEventListener('cancel', handleCancel);
            if (input.parentNode) {
                input.parentNode.removeChild(input);
            }
        };

        const handleChange = async (event: Event) => {
            if (settled) return;
            const files = (event.target as HTMLInputElement).files;
            if (!files || files.length === 0) {
                settled = true;
                cleanup();
                rejectCancelled();
                return;
            }

            settled = true;
            try {
                const originalFile = files[0];
                const file = await postProcessWebImage(originalFile);
                cleanup();
                resolve(file);
            } catch (error) {
                cleanup();
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        };

        const handleCancel = () => {
            if (settled) return;
            settled = true;
            cleanup();
            rejectCancelled();
        };

        input.addEventListener('change', handleChange);
        input.addEventListener('cancel', handleCancel);
        if (enableDismissHeuristic) {
            document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('blur', onWindowBlur, true);
            window.addEventListener('focus', onWindowFocus, true);
            Object.assign(input.style, {
                position: 'fixed',
                top: '0',
                left: '0',
                width: '1px',
                height: '1px',
                opacity: '0.01',
            });
        } else {
            input.style.position = 'absolute';
            input.style.left = '-9999px';
            input.style.top = '-9999px';
        }
        document.body.appendChild(input);

        try {
            input.click();
        } catch (error) {
            settled = true;
            cleanup();
            reject(new Error(`Failed to trigger file input: ${error}`));
        }
    });
};

/**
 * 上传文件并获取对象键（带重试机制）
 * @param file 要上传的文件
 * @param tag 文件标签
 * @param maxRetries 最大重试次数，默认3次
 * @returns 包含对象键的Promise
 */
// export const fileUpload = async (
//     file: File,
//     tag?: string,
//     maxRetries: number = 3
// ): Promise<{ objectKey: string }> => {
//     // 获取上传URL（不需要重试）
//     const { data } = await client.mutate({
//         mutation: GET_FILE_UPLOAD_URL,
//         variables: {
//             input: {
//                 filename: file.name,
//                 contentType: file.type,
//                 usage: tag ? `general/${tag}` : 'general'
//             }
//         }
//     });
//
//     if (!data?.getFileUploadUrl?.uploadUrl || !data?.getFileUploadUrl?.objectKey) {
//         throw new Error('Failed to get upload URL from server');
//     }
//
//     const { uploadUrl, objectKey } = data.getFileUploadUrl;
//
//     // 文件上传部分（带重试机制）
//     let lastError: Error | null = null;
//
//     for (let attempt = 0; attempt < maxRetries; attempt++) {
//         try {
//             const response = await fetch(uploadUrl, {
//                 method: 'PUT',
//                 body: file,
//                 headers: { 'content-type': 'multipart/form-data' },
//             });
//
//             if (!response.ok) {
//                 const errorText = await response.text().catch(() => 'Unknown error');
//                 const errorMsg = `Upload failed with status ${response.status}: ${errorText}`;
//                 remoteLogService.info(`App file upload HTTP error for '${tag ? `[${tag}]` : ''}${file.name}' on attempt ${attempt + 1}/${maxRetries}: ${errorMsg}`);
//                 throw new Error(errorMsg);
//             }
//
//             // 成功时记录日志（仅在重试后成功时）
//             if (attempt > 0) {
//                 remoteLogService.info(`File upload succeeded after ${attempt + 1} attempts: ${tag ? `[${tag}]` : ''}${file.name}`);
//             }
//
//             return {
//                 objectKey
//             };
//         } catch (error) {
//             lastError = error instanceof Error ? error : new Error(String(error));
//
//             // 记录重试日志
//             remoteLogService.info(`App file upload attempt ${attempt + 1}/${maxRetries} failed for file '${tag ? `[${tag}]` : ''}${file.name}': ${lastError.message}`);
//
//             // 如果是最后一次重试，不再延迟
//             if (attempt === maxRetries - 1) {
//                 break;
//             }
//
//             // 固定延迟100ms后重试
//             await delay(100);
//         }
//     }
//
//     // 所有重试都失败，记录最终错误并抛出异常
//     remoteLogService.info(`App file upload failed for '${tag ? `[${tag}]` : ''}${file.name}' after ${maxRetries} attempts: ${lastError?.message}`);
//     throw new Error(`Failed to upload file '${file.name}' after ${maxRetries} attempts: ${lastError?.message}`);
// };